import React from 'react';
import { Link } from 'react-router-dom';

const CooksmithPrivacy = () => {
  return (
    <div className="legal-page">
      <div className="legal-page__header">
        <div className="container">
          <Link to="/" className="legal-page__back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
          <span className="section__tag">Legal</span>
          <h1 className="legal-page__title">Privacy Policy</h1>
          <p className="legal-page__subtitle">
            Cooksmith by Wolf Technologies Ltd
          </p>
          <p className="legal-page__date">Last updated: 17 April 2026</p>
        </div>
      </div>

      <div className="container">
        <div className="legal-page__content">

          <section className="legal-section">
            <h2>1. Introduction</h2>
            <p>
              Cooksmith is a recipe management app developed and operated by Wolf Technologies Ltd
              ("we", "us", "our"). We are committed to protecting your privacy and handling your
              personal data responsibly. This Privacy Policy explains what data we collect, how we
              use it, and your rights under applicable data protection laws including the UK GDPR.
            </p>
            <p>
              This policy applies to the Cooksmith app (available on iOS and the web) and any
              associated services.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Data We Collect</h2>
            <p>
              We collect only the minimum data necessary to provide and improve the Cooksmith
              experience.
            </p>
            <h3>Account Information</h3>
            <ul>
              <li><strong>Email address</strong> — used for authentication and account recovery, collected during sign-up via AWS Cognito or Sign In with Apple.</li>
              <li><strong>Display name</strong> — an optional name you provide for your profile.</li>
            </ul>
            <h3>App Data</h3>
            <ul>
              <li><strong>Saved recipes</strong> — recipes you add to your personal cookbook.</li>
              <li><strong>Meal plans</strong> — weekly meal plans you create.</li>
              <li><strong>Grocery lists</strong> — shopping lists generated from your meal plans or created manually.</li>
              <li><strong>Preferences</strong> — your chosen measurement units (imperial or metric), dietary preferences, and app settings.</li>
              <li><strong>Community ratings</strong> — ratings you submit for recipes.</li>
              <li><strong>Cooking sessions</strong> — records of recipes you have cooked.</li>
            </ul>
            <h3>Subscription Information</h3>
            <ul>
              <li><strong>Subscription status</strong> — whether you hold a Free or Premium subscription, managed via Stripe or Apple In-App Purchase. We do not store your full payment card details.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>3. Data We Do NOT Collect</h2>
            <p>We want to be clear about what we do not collect:</p>
            <ul>
              <li><strong>Location data</strong> — we do not access or store your geographic location.</li>
              <li><strong>Contacts</strong> — we do not access your address book or contacts list.</li>
              <li><strong>Photos or camera data</strong> — we do not access your camera or photo library.</li>
              <li><strong>Health data</strong> — we do not access Apple Health, Google Fit, or any health-related information.</li>
              <li><strong>Browsing history</strong> — we do not track your activity outside of Cooksmith.</li>
              <li><strong>Advertising identifiers</strong> — we do not collect device advertising IDs or use any ad-tracking technology.</li>
              <li><strong>Analytics or telemetry</strong> — we do not use third-party analytics SDKs.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>4. How We Use Your Data</h2>
            <p>We use your data solely to provide the Cooksmith service:</p>
            <ul>
              <li><strong>Account management</strong> — to create and maintain your account, authenticate you, and enable password recovery.</li>
              <li><strong>Recipe sync</strong> — to synchronise your saved recipes, cookbook, and preferences across your devices.</li>
              <li><strong>Meal planning</strong> — to store and retrieve your meal plans and generate grocery lists.</li>
              <li><strong>Subscription management</strong> — to verify your subscription status and grant access to Premium features.</li>
              <li><strong>Service improvement</strong> — to understand aggregate usage patterns (e.g. which cuisines are most popular) in order to improve the recipe catalogue. This is done using anonymised, aggregated data only.</li>
            </ul>
            <p>We do not use your data for advertising, profiling, or automated decision-making.</p>
          </section>

          <section className="legal-section">
            <h2>5. Data Storage & Security</h2>
            <ul>
              <li><strong>Region</strong> — all data is stored in the AWS EU (London) region (eu-west-2), located in the United Kingdom.</li>
              <li><strong>Encryption at rest</strong> — all data stored in DynamoDB and S3 is encrypted at rest using AWS-managed encryption keys (AES-256).</li>
              <li><strong>Encryption in transit</strong> — all communication between your device and our servers uses HTTPS (TLS 1.2+).</li>
              <li><strong>Authentication</strong> — user authentication is handled by AWS Cognito, which provides secure token-based access with bcrypt password hashing.</li>
              <li><strong>Access control</strong> — API access is restricted to authenticated users via Cognito authorisers. Each user can only access their own data.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>6. Third-Party Services</h2>
            <p>We use the following third-party services to operate Cooksmith:</p>
            <ul>
              <li><strong>AWS Cognito</strong> — user authentication and account management. Receives email address and hashed password.</li>
              <li><strong>AWS DynamoDB</strong> — storage of recipes, meal plans, grocery lists, and preferences. Stores all app data associated with your user ID.</li>
              <li><strong>AWS S3</strong> — storage of recipe images. Images are public recipe photos, not user-uploaded.</li>
              <li><strong>Stripe</strong> — subscription billing and payment processing (web). Receives email address and subscription status. Stripe handles all card details directly — we never see or store your full card number.</li>
              <li><strong>Apple In-App Purchase</strong> — subscription billing and payment processing (iOS). Apple handles all payment details; we receive only transaction receipts.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>7. Data Sharing</h2>
            <p>
              <strong>We do not sell your personal data.</strong> We never have and never will.
            </p>
            <p>We share data with third parties only in the following limited circumstances:</p>
            <ul>
              <li><strong>Payment processors</strong> — we share your email address with Stripe (or Apple, for iOS subscriptions) solely for the purpose of managing your subscription and processing payments.</li>
              <li><strong>Legal obligations</strong> — we may disclose data if required to do so by law, regulation, or court order.</li>
            </ul>
            <p>We do not share your data with advertisers, data brokers, or any other third parties.</p>
          </section>

          <section className="legal-section">
            <h2>8. Data Retention & Deletion</h2>
            <ul>
              <li><strong>Active accounts</strong> — your data is retained for as long as your account remains active.</li>
              <li><strong>Account deletion</strong> — if you request deletion of your account, we will permanently remove all of your personal data and app data within 30 days. This includes your account credentials, saved recipes, meal plans, grocery lists, and preferences.</li>
              <li><strong>Subscription cancellation</strong> — cancelling your subscription does not delete your account. Your data is retained and you revert to the Free tier. To delete your data, you must separately request account deletion.</li>
            </ul>
            <p>
              To request account deletion, email us at{' '}
              <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a> with the
              subject line "Account Deletion Request".
            </p>
          </section>

          <section className="legal-section">
            <h2>9. Your Rights (GDPR)</h2>
            <p>
              Under the UK GDPR, you have the following rights regarding your personal data:
            </p>
            <ul>
              <li><strong>Access</strong> — you can request a copy of the personal data we hold about you.</li>
              <li><strong>Rectification</strong> — you can request correction of any inaccurate data.</li>
              <li><strong>Erasure</strong> — you can request deletion of your personal data ("right to be forgotten").</li>
              <li><strong>Data portability</strong> — you can request your data in a machine-readable format.</li>
              <li><strong>Restriction</strong> — you can request that we limit how we use your data.</li>
              <li><strong>Objection</strong> — you can object to processing of your data in certain circumstances.</li>
              <li><strong>Withdraw consent</strong> — where processing is based on consent, you can withdraw it at any time.</li>
            </ul>
            <p>
              To exercise any of these rights, contact us at{' '}
              <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a>. We will
              respond to your request within 30 days.
            </p>
            <p>
              If you are not satisfied with our response, you have the right to lodge a complaint with
              the Information Commissioner's Office (ICO) at https://ico.org.uk.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. If we make material changes, we
              will notify you via email or an in-app notice. The "Last updated" date at the top of
              this page will always reflect the most recent revision.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Contact</h2>
            <p>
              If you have any questions about this Privacy Policy or how we handle your data, please
              contact us:
            </p>
            <div className="legal-contact">
              <p><strong>Wolf Technologies Ltd</strong></p>
              <p>United Kingdom</p>
              <p>
                Email:{' '}
                <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a>
              </p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default CooksmithPrivacy;
